import { BaseProvider } from './BaseProvider';
import { ProviderType, ProviderModel, GenerateRequest, GenerateResponse, PROVIDER_DEFAULTS } from './types';
import { logger } from '../loggingService';

export class OllamaProvider extends BaseProvider {
  readonly type: ProviderType = 'ollama';
  readonly name = 'Ollama';
  readonly supportsStreaming = true;
  readonly supportsFunctionCalling = false;
  readonly requiresApiKey = false;

  private getBaseUrl(): string {
    return this.config?.baseUrl || PROVIDER_DEFAULTS.ollama.baseUrl || 'http://localhost:11434';
  }

  async testConnection(): Promise<{ success: boolean; message: string; models?: ProviderModel[] }> {
    try {
      const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/api/tags`, {
        method: 'GET'
      }, 5000);

      if (!response.ok) {
        return { success: false, message: `Ollama server returned ${response.status}` };
      }

      const data = await response.json();
      const models = (data.models || []).map((m: any) => ({
        id: m.name,
        name: m.name,
        contextLength: m.details?.parameter_size ? parseInt(m.details.parameter_size) : undefined,
        description: m.details?.family || 'Local model'
      }));

      return {
        success: true,
        message: `Connected to Ollama. ${models.length} models available.`,
        models
      };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: false, message: 'Connection timeout. Is Ollama running?' };
      }
      return { success: false, message: `Cannot connect to Ollama: ${this.formatError(error)}` };
    }
  }

  async listModels(): Promise<ProviderModel[]> {
    try {
      const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/api/tags`, {
        method: 'GET'
      }, 5000);

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return (data.models || []).map((m: any) => ({
        id: m.name,
        name: m.name,
        contextLength: m.details?.context_length,
        description: `${m.details?.family || ''} ${m.details?.parameter_size || ''}`.trim()
      }));
    } catch {
      return [];
    }
  }

  async generate(request: GenerateRequest): Promise<GenerateResponse> {
    const model = this.config?.model || PROVIDER_DEFAULTS.ollama.model;

    const messages = [];

    if (request.systemPrompt) {
      messages.push({ role: 'system', content: request.systemPrompt });
    }

    messages.push(...request.messages.map(m => ({
      role: m.role,
      content: m.content
    })));

    const response = await this.fetchWithTimeout(`${this.getBaseUrl()}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        messages,
        stream: false,
        options: {
          temperature: request.temperature ?? 0.7,
          num_predict: request.maxTokens ?? 4096
        }
      })
    }, 120000);

    // Log the size of the request
    logger.info('OllamaProvider', 'Non-stream Request Initiated', {
      model,
      promptLength: JSON.stringify(messages).length,
      messageCount: messages.length
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Ollama error: ${error}`);
    }

    const data = await response.json();

    return {
      text: data.message?.content || '',
      model: data.model || model!,
      finishReason: data.done_reason || 'stop',
      usage: data.prompt_eval_count ? {
        promptTokens: data.prompt_eval_count,
        completionTokens: data.eval_count || 0,
        totalTokens: (data.prompt_eval_count || 0) + (data.eval_count || 0)
      } : undefined
    };
  }

  async *generateStream(request: GenerateRequest): AsyncGenerator<string, void, unknown> {
    const model = this.config?.model || PROVIDER_DEFAULTS.ollama.model;

    const messages = [];

    if (request.systemPrompt) {
      messages.push({ role: 'system', content: request.systemPrompt });
    }

    messages.push(...request.messages.map(m => ({
      role: m.role,
      content: m.content
    })));

    let response: Response;

    try {
      response = await this.streamFetch(`${this.getBaseUrl()}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          messages,
          stream: true,
          options: {
            temperature: request.temperature ?? 0.7,
            num_predict: request.maxTokens ?? 4096
          }
        })
      });

      // Log the size of the request to debug "context explosion"
      logger.info('OllamaProvider', 'Stream Request Initiated', {
        model,
        promptLength: JSON.stringify(messages).length,
        messageCount: messages.length
      });
    } catch (error: any) {
      if (this.isTauriEnvironment() &&
        (error?.message?.includes('CORS') ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('NetworkError'))) {
        console.warn('[Ollama] Streaming unavailable in desktop app, using non-streaming mode');
        console.info('[Ollama] To enable streaming: start Ollama with OLLAMA_ORIGINS=* ollama serve');
        const result = await this.generate(request);
        yield result.text;
        return;
      }
      throw error;
    }

    if (!response.ok) {
      throw new Error(`Ollama error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      if (this.isTauriEnvironment()) {
        console.warn('[Ollama] Stream reader unavailable in desktop app, using non-streaming mode');
        console.info('[Ollama] To enable streaming: start Ollama with OLLAMA_ORIGINS=* ollama serve');
        const result = await this.generate(request);
        yield result.text;
        return;
      }
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const data = JSON.parse(line);
          if (data.message?.content) {
            logger.debug('OllamaProvider', 'Chunk received', { content: data.message.content });
            yield data.message.content;
          }
        } catch {
          continue;
        }
      }
    }
  }
}
